"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { startTransition, useDeferredValue } from "react";
import { RouteAnalyticsFilters } from "./route-analytics-filters";
import { RouteAnalyticsTable } from "./route-analytics-table";
import { useRouteAnalytics } from "@/hooks/use-route-analytics";
import { serializeFilters, type UrlFilterState } from "@/hooks/use-url-filters";
import { useLocalStorage } from "@/hooks/use-local-storage";
import { LOCAL_STORAGE_KEYS } from "@/lib/constants";
import { ALLOWED_PAGE_SIZES, type AllowedPageSize } from "@/types/filters";
import {
  DEFAULT_ROUTE_ANALYTICS_PRESET,
  ROUTE_ANALYTICS_TIME_PRESETS,
  type RouteAnalyticsTimePreset,
} from "@/types/route-analytics";
import {
  detectTimePreset,
  getCreatedRangeForHours,
  getDefaultRouteAnalyticsRange,
} from "@/utils/route-analytics-utils";
import { parseFlexibleDate } from "@/utils/date-utils";

function normalizePageSize(size: number): AllowedPageSize {
  if ((ALLOWED_PAGE_SIZES as readonly number[]).includes(size)) {
    return size as AllowedPageSize;
  }
  return 50;
}

function readInitialFilters(): UrlFilterState {
  if (typeof window === "undefined") {
    return getDefaultRouteAnalyticsRange();
  }
  const params = new URLSearchParams(window.location.search);
  const createdFrom = parseFlexibleDate(params.get("createdFrom") ?? undefined) ?? undefined;
  const createdTo = parseFlexibleDate(params.get("createdTo") ?? undefined) ?? undefined;
  const methods = (params.get("method") ?? "")
    .split(",")
    .map((m) => m.trim().toUpperCase())
    .filter(Boolean);
  const statusCodes = (params.get("statusCode") ?? "")
    .split(",")
    .map((v) => Number.parseInt(v, 10))
    .filter((n) => Number.isFinite(n) && n >= 100 && n <= 599);

  if (createdFrom || createdTo || methods.length || statusCodes.length) {
    return {
      ...(createdFrom ? { createdFrom } : {}),
      ...(createdTo ? { createdTo } : {}),
      ...(methods.length ? { methods } : {}),
      ...(statusCodes.length ? { statusCodes } : {}),
    };
  }
  return getDefaultRouteAnalyticsRange();
}

function RouteAnalyticsClientInner({
  basePath,
  analyticsApiPath,
  pageSizeStorageKey,
}: {
  basePath: string;
  analyticsApiPath: string;
  pageSizeStorageKey: string;
}) {
  const router = useRouter();
  // Seed a stable default on both server and client, then apply URL params after mount.
  const [filters, setFiltersState] = React.useState<UrlFilterState>(() => getDefaultRouteAnalyticsRange());
  const [routeSearch, setRouteSearch] = React.useState("");
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = useLocalStorage<number>(pageSizeStorageKey, 50);

  React.useEffect(() => {
    const fromUrl = readInitialFilters();
    // Only override the SSR default when the address bar actually carries filters
    // (e.g. returning from Route Details). Avoids a second fetch on fresh visits.
    if (typeof window === "undefined") return;
    if (!window.location.search) return;
    setFiltersState(fromUrl);
  }, []);

  const deferredSearch = useDeferredValue(routeSearch);
  const { data, isLoading, error, refetch } = useRouteAnalytics(filters, analyticsApiPath);

  const patchFilters = React.useCallback((patch: Partial<UrlFilterState>) => {
    setFiltersState((prev) => {
      const next: UrlFilterState = { ...prev, ...patch };
      for (const key of Object.keys(patch) as Array<keyof UrlFilterState>) {
        if (patch[key] === undefined) delete next[key];
      }
      return next;
    });
    startTransition(() => setPage(1));
  }, []);

  const activePreset: RouteAnalyticsTimePreset = detectTimePreset(
    filters.createdFrom,
    filters.createdTo,
  );

  const applyPreset = React.useCallback(
    (presetId: Exclude<RouteAnalyticsTimePreset, "custom">) => {
      const preset = ROUTE_ANALYTICS_TIME_PRESETS.find((p) => p.id === presetId)!;
      const range = getCreatedRangeForHours(preset.hours);
      patchFilters({
        createdFrom: range.createdFrom,
        createdTo: range.createdTo,
        updatedFrom: undefined,
        updatedTo: undefined,
      });
    },
    [patchFilters],
  );

  const toggleMethod = React.useCallback(
    (method: string) => {
      const current = filters.methods ?? [];
      const next = current.includes(method)
        ? current.filter((m) => m !== method)
        : [...current, method];
      patchFilters({ methods: next.length ? next : undefined });
    },
    [filters.methods, patchFilters],
  );

  const toggleStatusCode = React.useCallback(
    (statusCode: number) => {
      const current = filters.statusCodes ?? [];
      const next = current.includes(statusCode)
        ? current.filter((c) => c !== statusCode)
        : [...current, statusCode];
      patchFilters({ statusCodes: next.length ? next : undefined });
    },
    [filters.statusCodes, patchFilters],
  );

  const resetFilters = React.useCallback(() => {
    setRouteSearch("");
    const range = getCreatedRangeForHours(
      ROUTE_ANALYTICS_TIME_PRESETS.find((p) => p.id === DEFAULT_ROUTE_ANALYTICS_PRESET)!.hours,
    );
    setFiltersState({ createdFrom: range.createdFrom, createdTo: range.createdTo });
    startTransition(() => setPage(1));
  }, []);

  const filteredRoutes = React.useMemo(() => {
    const routes = data?.routes ?? [];
    const query = deferredSearch.trim().toLowerCase();
    if (!query) return routes;
    return routes.filter((row) => row.route.toLowerCase().includes(query));
  }, [data?.routes, deferredSearch]);

  const handleRowClick = React.useCallback(
    (route: string, totalRequests: number) => {
      const next: UrlFilterState = {
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
        methods: filters.methods,
        statusCodes: filters.statusCodes,
        routes: [route],
      };
      // Include totalCount so the details page can calculate pages without extra API calls
      const qs = serializeFilters(next);
      const separator = qs.includes("?") ? "&" : "?";
      router.push(`${basePath}/details${qs}${separator}totalCount=${totalRequests}`);
    },
    [basePath, filters.createdFrom, filters.createdTo, filters.methods, filters.statusCodes, router],
  );

  const handlePageChange = React.useCallback((nextPage: number) => {
    startTransition(() => setPage(nextPage));
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden p-4 sm:p-6">
      <div className="shrink-0 space-y-1">
        <p className="text-sm text-muted-foreground">
          One row per unique route for the selected time range.
        </p>
        <RouteAnalyticsFilters
          routeSearch={routeSearch}
          onRouteSearchChange={setRouteSearch}
          methods={filters.methods ?? []}
          statusCodes={filters.statusCodes ?? []}
          createdFrom={filters.createdFrom}
          createdTo={filters.createdTo}
          activePreset={activePreset}
          onApplyPreset={applyPreset}
          onToggleMethod={toggleMethod}
          onToggleStatusCode={toggleStatusCode}
          onCustomRange={(from, to) =>
            patchFilters({
              createdFrom: from,
              createdTo: to,
              updatedFrom: undefined,
              updatedTo: undefined,
            })
          }
          onReset={resetFilters}
          onRefresh={refetch}
          isRefreshing={isLoading}
        />
      </div>

      <RouteAnalyticsTable
        rows={filteredRoutes}
        isLoading={isLoading}
        error={error}
        onRetry={refetch}
        onRowClick={handleRowClick}
        resultMayBePartial={data?.resultMayBePartial ?? false}
        pageSize={normalizePageSize(pageSize)}
        onPageSizeChange={(size) => {
          setPageSize(normalizePageSize(size));
          handlePageChange(1);
        }}
        currentPage={page}
        onPageChange={handlePageChange}
      />
    </div>
  );
}

export function RouteAnalyticsClient({
  basePath = "/logs/route-analytics",
  analyticsApiPath = "/api/analytics/routes",
  pageSizeStorageKey = `${LOCAL_STORAGE_KEYS.pageSize}:route-analytics:logs`,
}: {
  basePath?: string;
  analyticsApiPath?: string;
  pageSizeStorageKey?: string;
} = {}) {
  return (
    <RouteAnalyticsClientInner
      basePath={basePath}
      analyticsApiPath={analyticsApiPath}
      pageSizeStorageKey={pageSizeStorageKey}
    />
  );
}
